import { Resource } from "../model/Resource.js";
import { ArrowState } from "./views/AbstractContentView.js";

export class PdfViewer {
    public onChangeSupportsArrows = e5.Signal.create<[ArrowState]>();

    public element: HTMLDivElement;

    private _scrollView: e5.ScrollView;
    private _pdf: any = null;
    private _thumbs: HTMLDivElement;
    private _thumbHeight: number = 150;
    private _thumbWidth: number = 150;
    private _currentPageNumber: number = 1;
    private _totalPages: number = 0;
    private _pdfView: HTMLDivElement;
    private _layout: "horz" | "vert" = "horz";

    constructor() {
        this.element = document.createElement("div");
        this.element.className = "pdf-viewer";
        this.element.setAttribute("data-layout", this._layout);
        this.element.innerHTML = `
        <div class="pdf-view loading" data-layout="${this._layout}"></div>
        <div class="pdf-thumbs scrollbar" data-layout="${this._layout}"></div>
        `;
        this._thumbs = this.element.querySelector(".pdf-thumbs");
        this._pdfView = this.element.querySelector(".pdf-view");

        this._scrollView = new e5.ScrollView();
        this._scrollView.wheel = e5.ScrollViewWheel.Scroll;
        this._scrollView.minScale = e5.ScrollViewScale.Original;
        this._scrollView.maxScale = 2;
        this._pdfView.appendChild(this._scrollView.element);

        (pdfjsLib as any).GlobalWorkerOptions.workerSrc = Resource.BASE + "js/pdf.worker.js";

        $(this._thumbs).on("click", "[data-pdfpage]", (e) => {
            var pageNumber = parseInt(e.currentTarget.getAttribute("data-pdfpage"));
            this.openPageNumber(pageNumber);
        });

        var swipe = new e5.Swipe(this.element);
        swipe.onSwipe.add((direction) => {
            // console.log(this._scrollView.hasOverflowHorizontal);
            if (this._scrollView.hasOverflowHorizontal() || this._totalPages <= 1)
                return;

            if (direction === "left") {
                this.next();
                this._scrollView.cancel();
            }
            else if (direction === "right") {
                this.prev();
                this._scrollView.cancel();
            }
        });
    }

    public get active(): boolean {
        return this.element.classList.contains("active");
    }

    public set active(active: boolean) {
        this.element.classList.toggle("active", active);
    }

    public async open(url: string) {
        this.active = true;

        this._currentPageNumber = 1;
        this._totalPages = 0;

        //Clear
        this._scrollView.content = null;
        this._thumbs.innerHTML = "";
        if (this._pdf) {
            this._pdf.destroy();
            this._pdf = null;
        }
        this._pdfView.classList.add("loading");

        //Load pdf
        var loadingTask: any = pdfjsLib.getDocument(url);
        loadingTask.promise.then(async (pdf: any) => {
            this._pdf = pdf;
            await this.openPageNumber(1);
            await this.createThumbs();
        });
    }

    public close(): void {
        this.active = false;
    }

    public next(): void {
        var pageNumber = this._currentPageNumber + 1;
        if (pageNumber > this._totalPages)
            pageNumber = 1;
        this.openPageNumber(pageNumber);
    }

    public prev(): void {
        var pageNumber = this._currentPageNumber - 1;
        if (pageNumber < 1)
            pageNumber = this._totalPages;
        this.openPageNumber(pageNumber);
    }

    private async createThumbs() {

        var count = this._pdf.numPages;

        //Get all pages (for thumbnail sizing)
        var pagePromises: Promise<any>[] = [];
        for (var i = 1; i <= count; ++i) {
            if (!this.active) {
                break;
            }
            pagePromises.push(this._pdf.getPage(i));
        }
        const pages = await Promise.all(pagePromises);

        this._totalPages = pages.length;

        this.onChangeSupportsArrows.dispatch(
            {
                left: this._totalPages > 1 ? (this._currentPageNumber <= 1 ? "skip" : "normal") : "none",
                right: this._totalPages > 1 ? (this._currentPageNumber >= this._totalPages ? "skip" : "normal") : "none",
                index: this._currentPageNumber - 1,
                total: this._totalPages
            });

        //Create empty thumbnails
        var html = "";
        for (var i = 1; i <= count; ++i) {
            if (!this.active) {
                break;
            }
            var page = pages[i - 1];

            var w = page.view[2];
            var h = page.view[3];
            var scale = 1;
            var style = "";
            if (this._layout === "vert") {
                scale = this._thumbHeight / h;
                style = `width: ${Math.round(w * scale)}px`;
            } else if (this._layout === "horz") {
                scale = this._thumbWidth / w;
                style = `height: ${Math.round(h * scale)}px`;
            }
            html += `<div class="pdf-thumb loading" data-pdfpage="${i}" data-layout="${this._layout}" style="${style}"></div>`;
        }
        this._thumbs.innerHTML = html;
        this.selectThumbnail(this._currentPageNumber);

        //Render each thumbnail
        for (var i = 1; i <= count; ++i) {
            if (!this.active) {
                break;
            }

            var canvas = await this.render(i, this._thumbHeight, this._thumbHeight, "height");
            var thumb = this._thumbs.querySelector(`[data-pdfpage="${i}"]`) as HTMLDivElement;
            thumb.style.width = "auto";
            thumb.classList.remove("loading");
            thumb.appendChild(canvas);
        }
    }

    private async openPageNumber(pageNumber: number) {
        this._currentPageNumber = pageNumber;
        this.selectThumbnail(this._currentPageNumber);

        this._pdfView.classList.add("loading");

        this.onChangeSupportsArrows.dispatch(
            {
                left: this._totalPages > 1 ? (this._currentPageNumber <= 1 ? "skip" : "normal") : "none",
                right: this._totalPages > 1 ? (this._currentPageNumber >= this._totalPages ? "skip" : "normal") : "none",
                index: this._currentPageNumber - 1,
                total: this._totalPages
            });

        var w = this._pdfView.offsetWidth;
        try {
            // var canvas = await this.render(pageNumber, 1920 * 2, 1080 * 2, "cover");
            var canvas = await this.render(pageNumber, w, w, "width");
            this._scrollView.y = 0;
            this._scrollView.content = {
                element: canvas,
                width: canvas.width,
                height: canvas.height
            };
            this._pdfView.classList.remove("loading");
        } catch (error) {
            // console.warn(error);
        }
    }

    private selectThumbnail(pageNumber: number): void {
        $(".pdf-thumb", this._thumbs).removeClass("selected");
        var element = this._thumbs.querySelector(`[data-pdfpage="${pageNumber}"]`) as HTMLDivElement;
        if (element) {
            element.classList.add("selected");
            if (this._layout === "vert") {
                var scrollLeft = (element.offsetLeft + element.offsetWidth * 0.5) - this._thumbs.offsetWidth * 0.5;
                scrollLeft = e5.Calc.clamp(scrollLeft, 0, this._thumbs.scrollWidth - this._thumbs.offsetWidth);
                gsap.to(this._thumbs, { duration: 0.5, scrollLeft: scrollLeft });
            } else if (this._layout === "horz") {
                var scrollTop = (element.offsetTop + element.offsetHeight * 0.5) - this._thumbs.offsetHeight * 0.5;
                scrollTop = e5.Calc.clamp(scrollTop, 0, this._thumbs.scrollHeight - this._thumbs.offsetHeight);
                gsap.to(this._thumbs, { duration: 0.5, scrollTop: scrollTop });
            }
        }
    }

    private async render(pageNumber: number, width: number, height: number, scaleMode: "contain" | "cover" | "height" | "width" = "cover"): Promise<HTMLCanvasElement> {
        return new Promise((resolve, reject) => {

            this._pdf.getPage(pageNumber).then((page: any) => {
                var w = page.view[2];
                var h = page.view[3];
                var scale = 1;

                if (scaleMode === "cover") {
                    scale = Math.max(width / w, height / h);
                } else if (scaleMode === "contain") {
                    scale = Math.min(width / w, height / h);
                } else if (scaleMode === "height") {
                    scale = height / h;
                } else if (scaleMode === "width") {
                    scale = width / w;
                }
                var viewport = page.getViewport({ scale: scale });
                var canvas = document.createElement("canvas");
                var context = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;
                var renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                };
                page.render(renderContext).promise.then(
                    () => {
                        resolve(canvas);
                    },
                    () => {
                        reject();
                    });
            }, () => {
                reject();
            });
        });
    }
}